import { Injectable } from '@angular/core';
import { HttpLogado } from '../HttpLogado';
import { environment } from '../../environments/environment';
import { Contact } from '../../classes/contact';
import { LocalStorageService } from '../local-storage/local-storage.service';
import convertHttpErrorToLocal from 'src/utils/convertHttpErrorToLocal';
import { Task } from 'src/classes/task';

@Injectable()
export class ContactService {

  constructor(
    private http: HttpLogado,
    private localStorage: LocalStorageService
  ) { }

  async sendPdfPassword(contactEmail: string, pdfPassword: string) {
    try {
      const data = { contactEmail, pdfPassword };
      return await this.http.post(`${environment.baseUrl}/contacts/send-pdf-password`, data).toPromise();
    } catch(err) {
      convertHttpErrorToLocal(err);
    }
  }

  async getLinkedTasks(idContact: string) {
    try {
      return await this.http.get(`${environment.baseUrl}/contacts/${idContact}/tasks`).toPromise();
    } catch(err) {
      convertHttpErrorToLocal(err);
    }
  }

  async fetchContactList(filter?: string, offset?: number, limit?: number) {
    let data: any;
    if (filter || offset || limit) {
      data = await this.http.get(`${environment.baseUrl}/contacts/filter/${filter}/offset/${offset}/limit/${limit}`).toPromise();
    } else {
      data = await this.http.get(`${environment.baseUrl}/contacts`).toPromise();
    }
    return data;
  }

  /**
   * Retrieves the list of contacts from local cache if possible, otherwise loads from server
   * @param filter The filter to be applied
   * @param offset The offset
   * @param limit The length limit
   * @param force Whether to use or not local cache
   */
  async listContacts(
    filter?: string,
    offset?: number,
    limit?: number,
    force?: boolean
  ): Promise<{data: Contact[], total: number}> {
    if (filter || offset || limit || force) {
      return await this.fetchContactList(filter, offset, limit);
    }
    const persistMinutes = 0;
    const data = await this.localStorage.generateOrCache(async () => {
      return JSON.stringify(await this.fetchContactList(filter, offset, limit));
    }, 'contact-list', persistMinutes / 60);
    return JSON.parse(data);
  }

  searchContacts(expr) {
    return this.http.get(`${environment.baseUrl}/contacts/search/${expr}`);
  }

  clearCache() {
    this.localStorage.clearItem('contact-list');
  }

  async addContact(data: Contact) {
    this.clearCache();
    const contact = Object.assign({}, data);
    delete contact.idContact;
    try {
      const response = await this.http.post(`${environment.baseUrl}/contacts`, contact).toPromise();
      return response;
    } catch(err) {
      convertHttpErrorToLocal(err);
    }
  }

  async editContact(data: Contact) {
    this.clearCache();
    const id = data.idContact;
    const contact = Object.assign({}, data);
    delete contact.idContact;
    delete contact.emailUser;
    try {
      const response = await this.http.put(`${environment.baseUrl}/contacts/${id}`, contact).toPromise();
      return response;
    } catch(err) {
      convertHttpErrorToLocal(err);
    }
  }

  removeContact(idContact: number) {
    this.clearCache();
    return this.http.delete(`${environment.baseUrl}/contacts/${idContact}`);
  }

  removeBulkContact(contacts: Array<number>) {
    this.clearCache();
    return this.http.post(`${environment.baseUrl}/contacts/bulk/remove`, {contacts});
  }

  postFile(fileToUpload: File, isEnabledUpdateBulk) {
    this.clearCache();
    const formData: FormData = new FormData();
    formData.append('sampleFile', fileToUpload, fileToUpload.name);
    formData.append('updateContact', isEnabledUpdateBulk);
    return this.http.post(`${environment.baseUrl}/contacts/bulk`, formData);
  }

  async getTags(): Promise<{tag: string, count: number}[]> {
    try {
      const tags = await this.http.get(`${environment.baseUrl}/contacts/tags`).toPromise();
      if (!(tags instanceof Array)) {
        throw new Error(`Server's tag endpoint returned unexpected type, expected "array", got "${typeof tags}"`);
      }
      if (tags.length === 0) {
        return [];
      }
      if (typeof tags[0] === "string") {
        return tags.map(tag => ({tag, count: 1}));
      }
      if (typeof tags[0].tags === "string" && typeof tags[0].size === "number") {
        return tags.map(tag => ({
          tag: tag.tags,
          count: tag.size
        }));
      }
      console.log(tags);
      throw new Error("Server returned unknown tag array type");
    } catch(err) {
      convertHttpErrorToLocal(err);
    }
  }
}
